import { axiosClient } from '@/libs/axios-client';
import { useAuthStore } from '@/store/useAuthStore';
import { useTimeTrackingStore } from '@/store/useTimeTrackingStore';

const resolveCompany = (existing) => {
  if (existing && existing !== 'all') return existing;
  // Prefer the company in the current URL over the persisted store. The
  // employee timesheet calendar always carries `?company=` (the viewed
  // employee's company) but never updates the time-tracking store, whose
  // persisted `selectedCompany` can be stale (e.g. left over from the time
  // tracking app, or a company that no longer exists after a reseed). The
  // dedicated time-tracking pages keep the URL and the store in sync, so URL
  // first yields the same value there.
  const urlCompany = new URLSearchParams(window.location.search).get('company');
  if (urlCompany && urlCompany !== 'all') return urlCompany;
  const { selectedCompany } = useTimeTrackingStore.getState();
  if (selectedCompany) return selectedCompany;
  const { user } = useAuthStore.getState();
  if (user?.companies?.length) return user.companies[0];
  return null;
};

const withCompany = (params = {}) => ({
  ...params,
  company: resolveCompany(params.company),
});
const withCompanyBody = (payload = {}) => ({
  ...payload,
  company: resolveCompany(payload.company),
});

// Projects
export const createProject = async (payload) => {
  const response = await axiosClient.post(
    '/time-tracking/projects',
    withCompanyBody(payload)
  );
  return response.data;
};

export const getProjects = async (params) => {
  const response = await axiosClient.get('/time-tracking/projects', {
    params: withCompany(params),
  });
  return response.data;
};

export const getProjectById = async (projectId) => {
  const response = await axiosClient.get(
    `/time-tracking/projects/${projectId}`,
    { params: withCompany() }
  );
  return response.data;
};

export const updateProject = async (projectId, payload) => {
  const response = await axiosClient.put(
    `/time-tracking/projects/${projectId}`,
    withCompanyBody(payload)
  );
  return response.data;
};

export const deleteProject = async (projectId) => {
  const response = await axiosClient.delete(
    `/time-tracking/projects/${projectId}`,
    { params: withCompany() }
  );
  return response.data;
};

export const getProjectMembers = async (projectId, params) => {
  const response = await axiosClient.get(
    `/time-tracking/projects/${projectId}/members`,
    { params: withCompany(params) }
  );
  return response.data;
};

export const getProjectTasks = async (projectId, params) => {
  const response = await axiosClient.get(
    `/time-tracking/projects/${projectId}/tasks`,
    { params: withCompany(params) }
  );
  return response.data;
};

// Project Tasks
export const addTask = async (projectId, payload) => {
  const response = await axiosClient.post(
    `/time-tracking/projects/${projectId}/tasks`,
    withCompanyBody(payload)
  );
  return response.data;
};

export const updateTask = async (projectId, taskId, payload) => {
  const response = await axiosClient.put(
    `/time-tracking/projects/${projectId}/tasks/${taskId}`,
    withCompanyBody(payload)
  );
  return response.data;
};

export const deleteTask = async (projectId, taskId) => {
  const response = await axiosClient.delete(
    `/time-tracking/projects/${projectId}/tasks/${taskId}`,
    { params: withCompany() }
  );
  return response.data;
};

// Project Members
export const addMember = async (projectId, payload) => {
  const response = await axiosClient.post(
    `/time-tracking/projects/${projectId}/members`,
    withCompanyBody(payload)
  );
  return response.data;
};

export const updateMember = async (projectId, employeeId, payload) => {
  const response = await axiosClient.put(
    `/time-tracking/projects/${projectId}/members/${employeeId}`,
    withCompanyBody(payload)
  );
  return response.data;
};

export const removeMember = async (projectId, employeeId) => {
  const response = await axiosClient.delete(
    `/time-tracking/projects/${projectId}/members/${employeeId}`,
    { params: withCompany() }
  );
  return response.data;
};

// Time Entries
export const createTimeEntry = async (payload) => {
  const response = await axiosClient.post(
    '/time-tracking/time-entries',
    withCompanyBody(payload)
  );
  return response.data;
};

export const getTimeEntries = async (params) => {
  const response = await axiosClient.get('/time-tracking/time-entries', {
    params: withCompany(params),
  });
  return response.data;
};

export const getTimeEntryById = async (entryId) => {
  const response = await axiosClient.get(
    `/time-tracking/time-entries/${entryId}`,
    { params: withCompany() }
  );
  return response.data;
};

export const updateTimeEntry = async (entryId, payload) => {
  const response = await axiosClient.put(
    `/time-tracking/time-entries/${entryId}`,
    withCompanyBody(payload)
  );
  return response.data;
};

export const deleteTimeEntry = async (entryId) => {
  const response = await axiosClient.delete(
    `/time-tracking/time-entries/${entryId}`,
    { params: withCompany() }
  );
  return response.data;
};

export const getMonthlyTimeData = async (params) => {
  const response = await axiosClient.get(
    '/time-tracking/time-entries/monthly',
    { params: withCompany(params) }
  );
  return response.data;
};

// Timer
export const startTimer = async (payload) => {
  const response = await axiosClient.post(
    '/time-tracking/time-entries/timer/start',
    withCompanyBody(payload)
  );
  return response.data;
};

export const stopTimer = async () => {
  const response = await axiosClient.put(
    '/time-tracking/time-entries/timer/stop',
    withCompanyBody()
  );
  return response.data;
};

export const getActiveTimer = async () => {
  const response = await axiosClient.get(
    '/time-tracking/time-entries/timer/active',
    { params: withCompany() }
  );
  return response.data;
};

export const pauseTimer = async () => {
  const response = await axiosClient.put(
    '/time-tracking/time-entries/timer/pause',
    withCompanyBody()
  );
  return response.data;
};

export const resumeTimer = async () => {
  const response = await axiosClient.put(
    '/time-tracking/time-entries/timer/resume',
    withCompanyBody()
  );
  return response.data;
};

// Approval
export const submitEntries = async (payload) => {
  const response = await axiosClient.post(
    '/time-tracking/time-entries/submit',
    withCompanyBody(payload)
  );
  return response.data;
};

export const approveEntries = async (payload) => {
  const response = await axiosClient.post(
    '/time-tracking/time-entries/approve',
    withCompanyBody(payload)
  );
  return response.data;
};

export const rejectEntries = async (payload) => {
  const response = await axiosClient.post(
    '/time-tracking/time-entries/reject',
    withCompanyBody(payload)
  );
  return response.data;
};

export const getPendingEntries = async (params) => {
  const response = await axiosClient.get(
    '/time-tracking/time-entries/pending',
    { params: withCompany(params) }
  );
  return response.data;
};

// Reports
export const getReportByProject = async (params) => {
  const response = await axiosClient.get('/time-tracking/reports/by-project', {
    params: withCompany(params),
  });
  return response.data;
};

export const getReportByEmployee = async (params) => {
  const response = await axiosClient.get('/time-tracking/reports/by-employee', {
    params: withCompany(params),
  });
  return response.data;
};

export const getReportByClient = async (params) => {
  const response = await axiosClient.get('/time-tracking/reports/by-client', {
    params: withCompany(params),
  });
  return response.data;
};

export const getReportEntries = async (params) => {
  const response = await axiosClient.get('/time-tracking/reports/entries', {
    params: withCompany(params),
  });
  return response.data;
};

export const getReportSummary = async (params) => {
  const response = await axiosClient.get('/time-tracking/reports/summary', {
    params: withCompany(params),
  });
  return response.data;
};

export const getReportAnalytics = async (params) => {
  const response = await axiosClient.get('/time-tracking/reports/analytics', {
    params: withCompany(params),
  });
  return response.data;
};

export const exportReport = async (params) => {
  const response = await axiosClient.get('/time-tracking/reports/export', {
    params: withCompany(params),
    responseType: 'blob',
  });
  return response.data;
};

// Report by specific customer
export const getReportByCustomerId = async (customerId, params) => {
  const response = await axiosClient.get(
    `/time-tracking/reports/by-customer/${customerId}`,
    { params: withCompany(params) }
  );
  return response.data;
};

export const getCustomerDetailByTask = async (customerId, params) => {
  const response = await axiosClient.get(
    `/time-tracking/reports/by-customer/${customerId}/detail`,
    { params: withCompany(params) }
  );
  return response.data;
};

export const generateCustomerSpecPdf = async (customerId, params) => {
  const response = await axiosClient.get(
    `/time-tracking/reports/by-customer/${customerId}/spec-pdf`,
    { params: withCompany(params) }
  );
  return response.data;
};

// Project Structure
export const getProjectStructure = async (params) => {
  const response = await axiosClient.get('/time-tracking/projects/structure', {
    params: withCompany(params),
  });
  return response.data;
};

export const reassignTaskMembers = async (payload) => {
  const response = await axiosClient.put(
    '/time-tracking/projects/reassign-members',
    withCompanyBody(payload)
  );
  return response.data;
};

// Project analytics export
export const exportProjectAnalytics = async (projectId, params) => {
  const response = await axiosClient.get(
    `/time-tracking/projects/${projectId}/export`,
    { params: withCompany(params), responseType: 'blob' }
  );
  return response.data;
};

// Copy entries
export const copyEntries = async (payload) => {
  const response = await axiosClient.post(
    '/time-tracking/time-entries/copy',
    withCompanyBody(payload)
  );
  return response.data;
};

// Auto-fill
export const autoFillEntries = async (payload) => {
  const response = await axiosClient.post(
    '/time-tracking/time-entries/auto-fill',
    withCompanyBody(payload)
  );
  return response.data;
};

// Sync
export const syncToPayroll = async (payload) => {
  const response = await axiosClient.post(
    '/time-tracking/time-entries/sync',
    withCompanyBody(payload)
  );
  return response.data;
};

const TimeTrackingService = {
  createProject,
  getProjects,
  getProjectById,
  getProjectMembers,
  getProjectTasks,
  updateProject,
  deleteProject,
  addTask,
  updateTask,
  deleteTask,
  addMember,
  updateMember,
  removeMember,
  createTimeEntry,
  getTimeEntries,
  getTimeEntryById,
  updateTimeEntry,
  deleteTimeEntry,
  getMonthlyTimeData,
  startTimer,
  stopTimer,
  pauseTimer,
  resumeTimer,
  getActiveTimer,
  submitEntries,
  approveEntries,
  rejectEntries,
  getPendingEntries,
  getReportByProject,
  getReportByEmployee,
  getReportByClient,
  getReportEntries,
  getReportSummary,
  getReportAnalytics,
  exportReport,
  exportProjectAnalytics,
  copyEntries,
  autoFillEntries,
  syncToPayroll,
  getReportByCustomerId,
  getCustomerDetailByTask,
  generateCustomerSpecPdf,
  getProjectStructure,
  reassignTaskMembers,
};

export default TimeTrackingService;
